import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { TextField, Box } from "@mui/material";

interface OtpInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
}

export interface OtpInputRef {
  focusFirst: () => void;
  focusFirstEmpty: () => void;
  getOtp: () => string;
  isComplete: () => boolean;
  clear: () => void;
}

const OtpInput = forwardRef<OtpInputRef, OtpInputProps>(
  ({ length = 6, onComplete, onChange }, ref) => {
    const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const focusInput = (index: number) => {
      inputRefs.current[index]?.focus();
    };

    useImperativeHandle(ref, () => ({
      focusFirst: () => focusInput(0),

      focusFirstEmpty: () => {
        const firstEmptyIndex = otp.findIndex((d) => d === "");
        focusInput(firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex);
      },

      getOtp: () => otp.join(""),
      isComplete: () => otp.every((d) => d !== ""),
      clear: () => {
        setOtp(new Array(length).fill(""));
        setTimeout(() => focusInput(0), 0);
      },
    }));

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number
    ) => {
      const value = e.target.value;
      if (!/^[0-9]$/.test(value)) return;

      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      onChange?.(newOtp.join(""));

      if (index < length - 1) focusInput(index + 1);

      if (newOtp.every((d) => d !== "")) {
        onComplete?.(newOtp.join(""));
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        const newOtp = [...otp];

        if (otp[index]) {
          newOtp[index] = "";
          setOtp(newOtp);
          onChange?.(newOtp.join(""));
          focusInput(index);
        } else if (index > 0) {
          focusInput(index - 1);
        }
      } else if (e.key === "ArrowLeft") {
        focusInput(Math.max(index - 1, 0));
      } else if (e.key === "ArrowRight") {
        focusInput(Math.min(index + 1, length - 1));
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);

      if (!pasted) return;

      const newOtp = pasted.split("").concat(new Array(length).fill("")).slice(0, length);

      setOtp(newOtp);
      onChange?.(newOtp.join(""));
      focusInput(Math.min(pasted.length, length - 1));

      if (pasted.length === length) {
        onComplete?.(pasted);
      }
    };

    return (
      <Box
        display="flex"
        gap={1}
        justifyContent="center"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": { flex: 1, minWidth: { xs: "2.5rem", sm: "3rem" } },
        }}
      >
        {otp.map((digit, index) => (
          <TextField
            key={index}
            value={digit}
            inputRef={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            size="small"
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              style: {
                textAlign: "center",
                width: 36,
                height: 36,
                padding: 0,
                fontSize: 16,
              },
            }}
          />
        ))}
      </Box>
    );
  }
);

export default OtpInput;
